import { useEffect, useState } from 'react';

type DpHiddenMode = 'one_show' | 'day_show';

interface UseDpHiddenOptions {
	/** Unique identifier for the localStorage key */
	id: string;
	/** Display mode: 'one_show' shows only once ever, 'day_show' shows once per day */
	mode?: DpHiddenMode;
	/** If true, auto-hides in useEffect. If false, requires manual hideComponent call */
	autoHidden?: boolean;
}

export const useDpHidden = ({ id, mode = 'day_show', autoHidden = true }: UseDpHiddenOptions) => {
	const [showComponent, setShowComponent] = useState(false);

	useEffect(() => {
		const storageKey = `dp_hidden_${id}`;
		const storedValue = localStorage.getItem(storageKey);
		const currentDate = new Date().toLocaleDateString();

		if (mode === 'one_show') {
			// Show only once ever
			if (!storedValue) {
				setShowComponent(true);
				// Auto-hide: register in localStorage immediately
				if (autoHidden) {
					localStorage.setItem(storageKey, 'shown');
				}
			}
		} else if (mode === 'day_show') {
			// Show once per day
			if (storedValue !== currentDate) {
				setShowComponent(true);
				// Auto-hide: register in localStorage immediately
				if (autoHidden) {
					localStorage.setItem(storageKey, currentDate);
				}
			}
		}
	}, [id, mode, autoHidden]);

	// Method to manually hide the component (when autoHidden is false)
	const hideComponent = () => {
		const storageKey = `dp_hidden_${id}`;
		const currentDate = new Date().toLocaleDateString();

		setShowComponent(false);

		if (mode === 'one_show') {
			localStorage.setItem(storageKey, 'shown');
		} else if (mode === 'day_show') {
			localStorage.setItem(storageKey, currentDate);
		}
	};

	return { showComponent, hideComponent };
};
